import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuth } from '../hooks/useAuth'

const WEBSOCKET_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:5001'

interface WebSocketContextType {
  socket: Socket | null
  isConnected: boolean
  connectionError: string | null
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}

interface WebSocketProviderProps {
  children: React.ReactNode
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    // Only connect if user is logged in
    if (!user) {
      // Clean up existing connection if user logs out
      if (socketRef.current) {
        console.log('🔌 WebSocket: User logged out, disconnecting...')
        socketRef.current.close()
        socketRef.current = null
        setIsConnected(false)
        setConnectionError(null)
      }
      return
    }

    const token = localStorage.getItem('token')

    if (!token) {
      setConnectionError('No authentication token')
      return
    }

    // If already connected, don't reconnect
    if (socketRef.current?.connected) {
      console.log('🔌 WebSocket: Already connected')
      return
    }

    console.log('🔌 WebSocket: Initializing connection on login...')

    // Create socket connection
    // Use WebSocket transport only — skip HTTP long-polling which triggers CORS/503
    // errors on Railway's load balancer before the protocol upgrade completes.
    const socket = io(WEBSOCKET_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: 15
    })

    // Connection events
    socket.on('connect', () => {
      console.log('✅ WebSocket connected - Socket ID:', socket.id)
      console.log('🔗 Connected to:', WEBSOCKET_URL)
      setIsConnected(true)
      setConnectionError(null)
    })

    socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason)
      setIsConnected(false)
      
      // If disconnect was due to error, show it
      if (reason === 'io server disconnect') {
        setConnectionError('Server disconnected')
      }
    })

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message)
      setConnectionError(error.message)
      setIsConnected(false)
    })

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Reconnection attempt #${attemptNumber}`)
    })

    socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Reconnected after ${attemptNumber} attempts`)
      setIsConnected(true)
      setConnectionError(null)
    })

    socket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed after all attempts')
      setConnectionError('Reconnection failed')
    })

    socketRef.current = socket

    // Cleanup on unmount or when user changes
    return () => {
      console.log('🔌 WebSocket: Cleaning up connection...')
      socket.close()
      socketRef.current = null
    }
  }, [user])

  return (
    <WebSocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected,
        connectionError
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}





